import http from '../../../services/api'
import type { APIResponse, Filters, APIPaginatedResponse, Pagination } from '../../../services/types'
import { getRequestParamString } from '../../../services/utils/utils'
import { ITEM_GROUP } from '../../../constants/ItemGroup'
import type { InventoryItemCategory } from '../types'

const apiPath = import.meta.env.VITE_API_PATH

const PRODUCT_ITEM_GROUP_ID = ITEM_GROUP.find((g) => g.name === 'Product')?.value

function resolveItemCategoryUuid(input: Pick<InventoryItemCategory, 'uuid'>): string {
  const uuid = input.uuid?.trim()
  if (!uuid) {
    throw new Error('Item category uuid is required')
  }
  return uuid
}

export type ItemCategoryListOptions = {
  /** When true (default), only product categories. When false, returns categories across all item groups. */
  productGroupOnly?: boolean
}

function itemGroupParamString(paramString: string, productGroupOnly: boolean): string {
  if (!productGroupOnly || PRODUCT_ITEM_GROUP_ID == null) {
    return ''
  }
  return `${paramString ? '&' : '?'}item_group_id=${PRODUCT_ITEM_GROUP_ID}`
}

async function getItemCategories(
  filters?: Partial<Filters>,
  pagination?: Partial<Pagination>,
  options?: ItemCategoryListOptions,
) {
  const paramString = getRequestParamString(filters, pagination)
  const productGroupOnly = options?.productGroupOnly !== false
  const itemGroupParam = itemGroupParamString(paramString, productGroupOnly)
  return await http.get<APIPaginatedResponse<InventoryItemCategory>>(
    `${apiPath}items/item-categories${paramString}${itemGroupParam}`,
  )
}

async function getItemCategoriesByItemGroupId(itemGroupId: string) {
  return await http.get<APIResponse<InventoryItemCategory[]>>(
    `${apiPath}items/item-categories/item-group/${itemGroupId}`,
  )
}

async function deleteItemCategory(itemCategoryUuid: string) {
  return await http.delete<APIResponse<boolean>>(
    `${apiPath}items/item-categories/${resolveItemCategoryUuid({ uuid: itemCategoryUuid })}`,
  )
}

async function createItemCategory(input: InventoryItemCategory) {
  const payLoad = {
    name: input.name,
    item_group_id: PRODUCT_ITEM_GROUP_ID,
    expires: input.expires,
    loyalty_applicable: input.loyalty_applicable,
    description: input.description,
  }

  return await http.post<APIResponse<InventoryItemCategory>>(`${apiPath}items/item-categories`, payLoad)
}

async function updateItemCategory(input: InventoryItemCategory) {
  const payLoad = {
    name: input.name,
    item_group_id: PRODUCT_ITEM_GROUP_ID,
    expires: input.expires,
    loyalty_applicable: input.loyalty_applicable,
    description: input.description,
  }

  return await http.patch<APIResponse<InventoryItemCategory>>(
    `${apiPath}items/item-categories/${resolveItemCategoryUuid(input)}`,
    payLoad,
  )
}

const itemCategoryService = {
  getItemCategories,
  getItemCategoriesByItemGroupId,
  deleteItemCategory,
  createItemCategory,
  updateItemCategory,
}

export default itemCategoryService
